(() => {
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const ICON_URL = 'https://cdn.shopify.com/s/files/1/0584/2570/4622/files/tc20-square-music-icon.svg?v=1786505200';
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const rand = (min, max) => min + Math.random() * (max - min);
  const randInt = (min, max) => Math.floor(rand(min, max + 1));

  // The uploaded product icon is placed so its physical discharge rod ends here.
  const EMITTER = { x: 250, y: 19 };
  // Original long visual was about 470 px from the emitter. 376 px is ~80%.
  const MAX_RADIUS = 376;
  // Bottom of the uploaded device icon in this 720 x 300 visual coordinate system.
  const GROUND_Y = 285;

  const sceneMarkup = `
    <div class="smbps-device-visual smviz-device" aria-hidden="true">
      <svg class="smviz-svg" viewBox="0 0 720 300" preserveAspectRatio="xMidYMid meet">
        <defs>
          <filter id="smvizGlow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="3.2"/>
          </filter>
          <filter id="smvizCoreGlow" x="-90%" y="-90%" width="280%" height="280%">
            <feGaussianBlur stdDeviation="1.05" result="b"/>
            <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        <image class="smviz-product-icon" data-smviz-icon x="35" y="15" width="216" height="270" preserveAspectRatio="xMidYMid meet"/>
        <g class="smviz-lightning-layer" data-smviz-lightning></g>
      </svg>
    </div>`;

  function pointPath(points) {
    if (!points || points.length < 2) return '';
    return points.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  }

  function angleBetween(a, b) {
    return Math.atan2(b.y - a.y, b.x - a.x);
  }

  function limitToRadius(point, maxRadius = MAX_RADIUS) {
    const dx = point.x - EMITTER.x;
    const dy = point.y - EMITTER.y;
    const d = Math.hypot(dx, dy);
    if (!d || d <= maxRadius) {
      return { x: clamp(point.x, 4, 716), y: clamp(point.y, 4, 296) };
    }
    const scale = maxRadius / d;
    return {
      x: clamp(EMITTER.x + dx * scale, 4, 716),
      y: clamp(EMITTER.y + dy * scale, 4, 296)
    };
  }

  function polarTarget(origin, angle, length) {
    return limitToRadius({
      x: origin.x + Math.cos(angle) * length,
      y: origin.y + Math.sin(angle) * length
    });
  }

  function buildJaggedToTarget(start, target, segments, jitter = .42) {
    const points = [{ x: start.x, y: start.y }];
    let current = { x: start.x, y: start.y };
    for (let i = 1; i < segments; i += 1) {
      const remaining = Math.max(1, segments - i + 1);
      const targetAngle = Math.atan2(target.y - current.y, target.x - current.x);
      const distance = Math.hypot(target.x - current.x, target.y - current.y);
      const step = distance / remaining * rand(.76, 1.24);
      const localAngle = targetAngle + rand(-jitter, jitter);
      current = limitToRadius({
        x: current.x + Math.cos(localAngle) * step,
        y: current.y + Math.sin(localAngle) * step
      });
      points.push(current);
    }
    points.push(limitToRadius(target));
    return points;
  }

  function branchTarget(origin, localAngle, length, depth) {
    const side = Math.random() < .5 ? -1 : 1;
    const spread = depth === 1 ? rand(.42, 1.08) : rand(.55, 1.24);
    return polarTarget(origin, localAngle + side * spread + rand(-.2, .2), length);
  }

  function addTrunk(paths, length, weight = 1, branchScale = 1, targetOverride = null) {
    // Normal discharge can move upward or downward, but remains primarily outward from the rod.
    const angle = rand(-1.02, 1.02);
    const target = targetOverride ? limitToRadius(targetOverride) : polarTarget(EMITTER, angle, length);
    const trunkLength = Math.hypot(target.x - EMITTER.x, target.y - EMITTER.y);
    const segments = trunkLength < 110 ? randInt(4, 6) : trunkLength < 255 ? randInt(6, 11) : randInt(10, 15);
    const mainPoints = buildJaggedToTarget(EMITTER, target, segments, trunkLength < 110 ? .52 : .38);
    paths.push({ points: mainPoints, depth: 0, weight });

    const branchCount = trunkLength < 110 ? randInt(0, 1) : trunkLength < 255 ? randInt(1, 3) : randInt(2, 5);
    for (let b = 0; b < branchCount && paths.length < 24; b += 1) {
      const idx = randInt(1, Math.max(1, mainPoints.length - 2));
      const origin = mainPoints[idx];
      const prev = mainPoints[Math.max(0, idx - 1)];
      const next = mainPoints[Math.min(mainPoints.length - 1, idx + 1)];
      const localAngle = angleBetween(prev, next);
      const branchLength = trunkLength * rand(.11, .27) * branchScale;
      const target1 = branchTarget(origin, localAngle, branchLength, 1);
      const branchPoints = buildJaggedToTarget(origin, target1, randInt(3, 6), .55);
      paths.push({ points: branchPoints, depth: 1, weight: rand(.48, .78) * weight });

      if (Math.random() < .38 && branchPoints.length > 3 && paths.length < 24) {
        const subIdx = randInt(1, branchPoints.length - 2);
        const subOrigin = branchPoints[subIdx];
        const subPrev = branchPoints[subIdx - 1];
        const subNext = branchPoints[Math.min(branchPoints.length - 1, subIdx + 1)];
        const subAngle = angleBetween(subPrev, subNext);
        const subTarget = branchTarget(subOrigin, subAngle, branchLength * rand(.3, .54), 2);
        paths.push({
          points: buildJaggedToTarget(subOrigin, subTarget, randInt(3, 5), .66),
          depth: 2,
          weight: rand(.28, .5) * weight
        });
      }
    }
  }

  function makeGroundTarget() {
    // 5% special event: the longest streamer terminates exactly at device-bottom level.
    // x is constrained so the endpoint still respects MAX_RADIUS.
    return { x: rand(420, 505), y: GROUND_Y };
  }

  function makeDischargePaths() {
    const paths = [];
    const roll = Math.random();

    // Primary-streamer distribution:
    // 15% short, 50% medium, 30% long-near-max, 5% ground-reaching long.
    // Long-near-max + ground-reaching = 35% near the new maximum class.
    if (roll < .15) {
      addTrunk(paths, rand(45, 110), rand(.72, .9), .8);
    } else if (roll < .65) {
      addTrunk(paths, rand(110, 255), rand(.82, 1), .92);
    } else if (roll < .95) {
      addTrunk(paths, rand(270, MAX_RADIUS), 1, 1);
    } else {
      const groundTarget = makeGroundTarget();
      const groundLength = Math.hypot(groundTarget.x - EMITTER.x, groundTarget.y - EMITTER.y);
      addTrunk(paths, groundLength, 1.04, 1, groundTarget);
    }

    // Occasional second streamer, always shorter than the main high-energy streamer.
    if (Math.random() < .36) {
      addTrunk(paths, rand(34, 145), rand(.44, .66), .7);
    }

    // Small corona / very short streamers keep the field non-uniform.
    const shortCount = randInt(1, 4);
    for (let i = 0; i < shortCount && paths.length < 26; i += 1) {
      const angle = rand(-1.18, 1.18);
      const target = polarTarget(EMITTER, angle, rand(18, 72));
      paths.push({
        points: buildJaggedToTarget(EMITTER, target, randInt(3, 5), .68),
        depth: 2,
        weight: rand(.22, .42)
      });
    }
    return paths;
  }

  function svgPath(d, className, width, opacity) {
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', d);
    path.setAttribute('class', className);
    path.setAttribute('vector-effect', 'non-scaling-stroke');
    path.style.strokeWidth = `${width}px`;
    path.style.opacity = String(opacity);
    return path;
  }

  function drawRandomDischarge(layer) {
    layer.replaceChildren();
    const paths = makeDischargePaths();
    paths.forEach(item => {
      const d = pointPath(item.points);
      const depthScale = item.depth === 0 ? 1 : item.depth === 1 ? .58 : .34;
      layer.appendChild(svgPath(
        d,
        `smviz-bolt-glow smviz-depth-${item.depth}`,
        rand(4.8, 7.2) * depthScale * item.weight,
        item.depth === 0 ? rand(.44, .68) : rand(.24, .48)
      ));
    });
    paths.forEach(item => {
      const d = pointPath(item.points);
      const depthScale = item.depth === 0 ? 1 : item.depth === 1 ? .62 : .38;
      layer.appendChild(svgPath(
        d,
        `smviz-bolt-core smviz-depth-${item.depth}`,
        rand(1.2, 2.1) * depthScale * item.weight,
        item.depth === 0 ? rand(.86, 1) : rand(.54, .86)
      ));
    });
  }

  function init(lab) {
    if (!lab || lab.dataset.randomVisualizerReady === '3') return;
    lab.dataset.randomVisualizerReady = '3';

    const display = lab.querySelector('.smbps-display');
    const valueNode = lab.querySelector('[data-bps-value]');
    if (!display || !valueNode) return;

    const old = display.querySelector('.smbps-device-visual');
    if (old) old.remove();
    display.insertAdjacentHTML('afterbegin', sceneMarkup);

    const icon = display.querySelector('[data-smviz-icon]');
    if (icon && ICON_URL) {
      icon.setAttribute('href', ICON_URL);
      icon.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', ICON_URL);
    }

    const layer = display.querySelector('[data-smviz-lightning]');
    const tool = lab.closest('[data-square-music-tool]');
    const audio = tool && tool.querySelector('[data-sm-audio]');

    let timer = 0;
    let fadeTimer = 0;
    let purgeTimer = 0;
    let active = false;

    const libraryPlaying = () => !!(audio && !audio.paused && !audio.ended);
    const bpsRunning = () => display.classList.contains('is-running');

    function visualInterval() {
      if (libraryPlaying()) return rand(72, 190);

      const bps = clamp(Number(valueNode.textContent) || 20, 1, 1000);

      // Stronger BPS-to-visual-rate coupling than before:
      // ~1 Hz at 1 BPS, ~7 Hz at 20, ~12.5 Hz at 100, ~33 Hz at 1000.
      // Capped for mobile rendering safety.
      const visualHz = bps <= 5 ? bps : 3 + .95 * Math.sqrt(bps);
      return 1000 / clamp(visualHz, .8, 34) * rand(.88, 1.12);
    }

    function purgeLayer() {
      if (layer) layer.replaceChildren();
    }

    function clearFlash(removeNow = false) {
      clearTimeout(fadeTimer);
      clearTimeout(purgeTimer);
      fadeTimer = 0;
      purgeTimer = 0;
      display.classList.remove('smviz-flash-on');
      if (!layer) return;
      layer.style.opacity = '0';
      if (removeNow) {
        purgeLayer();
      } else {
        // Keep only a very short optical afterglow, then remove the SVG paths entirely.
        purgeTimer = window.setTimeout(purgeLayer, 26);
      }
    }

    function flashOnce() {
      if (!active || !layer) return;

      drawRandomDischarge(layer);
      layer.style.opacity = String(rand(.82, 1));
      display.classList.add('smviz-flash-on');

      const visibleMs = libraryPlaying() ? rand(18, 34) : rand(15, 30);
      clearTimeout(fadeTimer);
      clearTimeout(purgeTimer);

      fadeTimer = window.setTimeout(() => {
        layer.style.opacity = '0';
        display.classList.remove('smviz-flash-on');
      }, visibleMs);

      purgeTimer = window.setTimeout(purgeLayer, visibleMs + 28);
      timer = window.setTimeout(flashOnce, visualInterval());
    }

    function syncActive() {
      const shouldRun = bpsRunning() || libraryPlaying();
      if (shouldRun === active) return;
      active = shouldRun;
      clearTimeout(timer);
      timer = 0;
      if (active) {
        flashOnce();
      } else {
        clearFlash(true);
      }
    }

    new MutationObserver(syncActive).observe(display, { attributes: true, attributeFilter: ['class'] });
    if (audio) {
      audio.addEventListener('play', syncActive);
      audio.addEventListener('pause', syncActive);
      audio.addEventListener('ended', syncActive);
    }
    window.addEventListener('warden:squareMusic', () => window.setTimeout(syncActive, 0));
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        active = false;
        clearTimeout(timer);
        timer = 0;
        clearFlash(true);
      } else {
        syncActive();
      }
    });
    window.addEventListener('pagehide', () => {
      active = false;
      clearTimeout(timer);
      clearFlash(true);
    });

    syncActive();
  }

  const initAll = (scope = document) => scope.querySelectorAll('[data-bps-lab]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', event => initAll(event.target));
})();
